/**
 * PathDiscovery — replaces the old forced BroadPathSelection.
 *
 * Entry points:
 *   /broad-path-selection              post-psychometric (skip link shown)
 *   /broad-path-selection?mode=add     from Career Paths page (no skip link)
 *
 * Phases:
 *   intent → search | discover_choice → psychometric | express → express_results
 */

import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { ChevronRight, ChevronLeft, Search, X, Check, Target, Sparkles, BookOpen, Cpu, BarChart2, BookMarked, Telescope, GraduationCap, Leaf, Scale } from "lucide-react";
import { BROAD_PATH_NODES } from "@/data/career-nodes";
import { getTopBroadPaths, BroadPathMatch } from "@/utils/broadPathMatcher";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import api from "@/hooks/api/apiClient";
import config from "../../../config";
import { TraitProfile } from "@/utils/traitScoring";
import traitList from "../../../traitList.json";
import { CAREER_TREE, getBroadNode, getAllNodes } from "@/data/career-tree";
import { selectCareerPath, traverseCareerPath } from "../../../Career Suggestion Engine/index";
import { toast } from "sonner";
import type { StudentProfile, GeneralTrait, ActiveNode, CareerPathNode } from "../../../Career Suggestion Engine/types";
import { pathEngine } from "@/hooks/usePathEngine";
import { Sidebar } from "@/components/ui/sidebar";
import ProtectedRoute from "@/components/ProtectedRoute";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type Phase = "intent" | "search" | "discover_choice" | "psychometric" | "express" | "express_results" | "field_funnel" | "spec_funnel";

// ─────────────────────────────────────────────────────────────────────────────
// Visual config
// ─────────────────────────────────────────────────────────────────────────────

type NodeVisual = { color: string; Icon: React.ComponentType<{ className?: string }> };

const NODE_VISUAL: Record<string, NodeVisual> = {
  broad_stem_tech:         { color: "bg-blue-50 border-blue-200 text-blue-700",       Icon: Cpu },
  broad_business_finance:  { color: "bg-amber-50 border-amber-200 text-amber-700",    Icon: BarChart2 },
  broad_humanities_social: { color: "bg-violet-50 border-violet-200 text-violet-700", Icon: BookMarked },
};

const DEFAULT_VISUAL: NodeVisual = { color: "bg-slate-100 border-slate-300 text-slate-700", Icon: Telescope };

// ─────────────────────────────────────────────────────────────────────────────
// Trait name lookup
// ─────────────────────────────────────────────────────────────────────────────

const TRAIT_NAME: Record<string, string> = Object.fromEntries(
  (traitList as Array<{ id: string; full_name: string }>).map((t) => [t.id, t.full_name])
);

// ─────────────────────────────────────────────────────────────────────────────
// Text-match scoring
// ─────────────────────────────────────────────────────────────────────────────

function tokenize(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter((w) => w.length > 2);
}

function scoreNode(node: CareerPathNode, tokens: string[]): number {
  if (!tokens.length) return 0;
  const name  = tokenize(node.name);
  const desc  = tokenize(node.description);
  const traits = node.traits.flatMap((tr) => tokenize(TRAIT_NAME[tr.traitId] ?? tr.name));
  let score = 0;
  for (const t of tokens) {
    if (name.some((w)   => w.includes(t))) score += 3;
    if (desc.some((w)   => w.includes(t))) score += 2;
    if (traits.some((w) => w.includes(t))) score += 1;
  }
  return score;
}

// Ranks the direct children of a node by how well the student's trait profile
// meets each child's minimum thresholds. Used by the funnel drill-down UI.
function rankChildren(parent: CareerPathNode, traitProfile: TraitProfile) {
  return parent.children
    .map((child) => {
      const met = child.traits.filter(
        (t) => (traitProfile[t.traitId] ?? 0) >= t.minimumThreshold,
      ).length;
      const matchScore = child.traits.length ? met / child.traits.length : 0.5;
      return { node: child, matchScore, isPositive: matchScore >= 0.6 };
    })
    .sort((a, b) => b.matchScore - a.matchScore);
}

// ─────────────────────────────────────────────────────────────────────────────
// Resolve which broad path a node belongs to (works for broad / field / spec)
// ─────────────────────────────────────────────────────────────────────────────

function resolveBroadIdFromNode(node: CareerPathNode): string | null {
  if (node.level === "broad") return node.nodeId;
  if (node.level === "field") return node.parentId ?? null;
  // specialization → field's parentId is the broad
  const field = getAllNodes().find((n) => n.nodeId === node.parentId);
  return field?.parentId ?? null;
}

/** How many picked nodes are genuinely new (not already in selectedBroadPaths). */
function countNewPickedPaths(picked: CareerPathNode[], alreadySaved: string[]): number {
  return picked.filter((n) => !alreadySaved.includes(n.nodeId)).length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Save / persist
// ─────────────────────────────────────────────────────────────────────────────

function useSaveSelection(traitProfile: TraitProfile) {
  const [saving, setSaving] = useState(false);

  // Returns true on success, false on failure. Caller handles navigation.
  const save = async (pickedNodes: CareerPathNode[]): Promise<boolean> => {
    if (!pickedNodes.length) return false;
    setSaving(true);
    try {
      const currentUser = AuthStore.getState().currentUser;
      if (!currentUser) throw new Error("Not authenticated");

      // Always load from StorageService — works in every mode and is the most
      // up-to-date source (mutations write here, not just to Zustand).
      const rawStudents = StorageService.read("students");
      const allStudents: any[] = Array.isArray(rawStudents) ? rawStudents : [];
      const protoIdx = allStudents.findIndex(
        (s) => s.userId === currentUser._id || s.email === currentUser.email,
      );

      // Fall back to Zustand store if StorageService has no record (non-prototype)
      let student: any = protoIdx >= 0 ? allStudents[protoIdx] : StudentStore.getState().student;

      if (!student) throw new Error("Student record not found");

      // Zustand is always kept current (deletions always write here regardless of mode).
      // StorageService may be stale in development mode, so prefer Zustand for live fields.
      const zustandStudent = StudentStore.getState().student as any;

      const generalTraits: GeneralTrait[] = (traitList as Array<{ id: string; full_name: string }>).map((t) => ({
        traitId: t.id,
        name: t.full_name,
        score: traitProfile[t.id] ?? student.traitProfile?.[t.id] ?? 0,
        updateCount: 1,
      }));

      let studentProfile: StudentProfile = {
        studentId: student._id,
        hasCompletedPsychometricTest: student.traitProfile != null,
        suggestionScope: "wide",
        generalTraits,
        activeNodes: zustandStudent?.activeNodes ?? student.activeNodes ?? [],
      };

      const newDisplayIds: string[] = [];      // actual picked node IDs → each stored as its own card
      const newEnrolledBroadIds: string[] = []; // broad IDs only → for pathEngine enrollment

      // Resolve the broad nodeId for any node level by climbing the tree
      const resolveBroadId = (node: CareerPathNode): string | null => {
        if (node.level === "broad") return node.nodeId;
        if (node.level === "field") return node.parentId ?? null;
        // specialization — find parent field, then the field's parent broad
        const fieldNode = node.parentId
          ? CAREER_TREE.flatMap((b) => b.children).find((f) => f.nodeId === node.parentId)
          : null;
        return fieldNode?.parentId ?? null;
      };

      // Compute existing BEFORE the loop — used both as the skip guard and the merge base.
      // Must read from Zustand (authoritative) since deletions always write there.
      const existing: string[] = zustandStudent?.selectedBroadPaths ?? student.selectedBroadPaths ?? [];

      // Process broader levels first so the ancestor chain is in place before
      // a child node tries to traverse into it.
      const levelOrder = { broad: 0, field: 1, specialization: 2 } as const;
      const sortedNodes = [...pickedNodes].sort(
        (a, b) => levelOrder[a.level] - levelOrder[b.level],
      );

      for (const node of sortedNodes) {
        const broadId = resolveBroadId(node);
        if (!broadId) continue;

        // Skip nodes that are already showing as a card on /career-path.
        // Do NOT skip based on activeNodes alone — the engine may still hold a broad
        // as "completed" after a child path was deleted, which would wrongly block re-adding it.
        if (existing.includes(node.nodeId)) continue;

        // Ensure the broad ancestor is activated
        if (!studentProfile.activeNodes.some((n: ActiveNode) => n.nodeId === broadId)) {
          const broadNode = getBroadNode(broadId);
          if (broadNode) {
            const r = selectCareerPath(studentProfile, broadNode);
            if (r.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: r.updatedActiveNodes };
          }
          // Only enroll the broad once, the first time it's activated
          if (!newEnrolledBroadIds.includes(broadId)) newEnrolledBroadIds.push(broadId);
        }

        // Each picked node becomes its own display card on /career-path
        if (!newDisplayIds.includes(node.nodeId)) newDisplayIds.push(node.nodeId);

        if (node.level === "field") {
          const broadEntry = studentProfile.activeNodes.find((n: ActiveNode) => n.nodeId === broadId);
          if (broadEntry?.status === "active") {
            // Broad is active → graduate: marks broad completed, field active
            const r = traverseCareerPath(studentProfile, node);
            if (r.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: r.updatedActiveNodes };
          } else {
            // Broad is completed (or just-skipped edge) → sibling add, no graduation
            const r = selectCareerPath(studentProfile, node);
            if (r.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: r.updatedActiveNodes };
          }
        } else if (node.level === "specialization") {
          const fieldNode = CAREER_TREE.flatMap((b) => b.children).find(
            (f) => f.nodeId === node.parentId,
          );
          if (fieldNode) {
            const fieldEntry = studentProfile.activeNodes.find((n: ActiveNode) => n.nodeId === fieldNode.nodeId);
            if (!fieldEntry) {
              // Field not yet in nodes: add it (sibling-style under completed broad), then graduate to spec
              const rf = selectCareerPath(studentProfile, fieldNode);
              if (rf.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: rf.updatedActiveNodes };
              const rs = traverseCareerPath(studentProfile, node);
              if (rs.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: rs.updatedActiveNodes };
            } else if (fieldEntry.status === "active") {
              // Field is active → graduate field → spec
              const r = traverseCareerPath(studentProfile, node);
              if (r.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: r.updatedActiveNodes };
            } else {
              // Field is completed → sibling spec add
              const r = selectCareerPath(studentProfile, node);
              if (r.updatedActiveNodes) studentProfile = { ...studentProfile, activeNodes: r.updatedActiveNodes };
            }
          }
        }
      }

      const merged = Array.from(new Set([...existing, ...newDisplayIds]));

      console.log("[BroadPathSelection] save debug", {
        pickedNodes: pickedNodes.map(n => `${n.nodeId}(${n.level})`),
        newDisplayIds,
        newEnrolledBroadIds,
        existing,
        merged,
        activeNodesCount: studentProfile.activeNodes.length,
      });

      const updatedFields = {
        selectedBroadPaths: merged,
        generalTraits: studentProfile.generalTraits,
        activeNodes: studentProfile.activeNodes,
        suggestionScope: "wide" as const,
      };

      // Local saves always happen first — these never fail.
      StudentStore.getState().updateStudent(updatedFields);

      if (protoIdx >= 0) {
        allStudents[protoIdx] = { ...allStudents[protoIdx], ...updatedFields, updatedAt: new Date() };
        StorageService.write("students", allStudents);
      }

      // Enroll in journey + seed goals for each newly activated broad path
      for (const broadId of newEnrolledBroadIds) {
        const broadNode = getBroadNode(broadId);
        const broadName = broadNode?.name ?? broadId;
        pathEngine.onPathEnrolled(broadId, broadName, 'broad');
      }

      // Backend sync is non-blocking — a network failure doesn't undo the local save.
      if (config.appMode !== "prototype") {
        api.patch("/students/me", {
          selectedBroadPaths: merged,
          activeNodes: studentProfile.activeNodes,
          generalTraits: studentProfile.generalTraits,
        }).catch((err) => {
          console.error("[BroadPathSelection] backend sync failed:", err);
          toast.error("Your selection was saved locally but couldn't sync to your account. Check your connection.");
        });
      }

      return true;
    } catch (err) {
      console.error("[BroadPathSelection] save failed:", err);
      toast.error("Something went wrong while saving your career paths. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { save, saving };
}

// ─────────────────────────────────────────────────────────────────────────────
// Level colour system
// ─────────────────────────────────────────────────────────────────────────────

const LEVEL_STYLE = {
  broad:          { bar: "bg-indigo-500",  pill: "bg-indigo-50 text-indigo-600 border-indigo-200",  label: "Domain" },
  field:          { bar: "bg-violet-500",  pill: "bg-violet-50 text-violet-600 border-violet-200",  label: "Field" },
  specialization: { bar: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-600 border-emerald-200", label: "Role" },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Node card — used in Search + ExpressResults
// ─────────────────────────────────────────────────────────────────────────────

function NodeCard({
  node,
  selected,
  onToggle,
  disabled,
}: {
  node: CareerPathNode;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  const visual = NODE_VISUAL[node.nodeId] ?? NODE_VISUAL[node.parentId ?? ""] ?? DEFAULT_VISUAL;
  const ls = LEVEL_STYLE[node.level];

  return (
    <button
      onClick={disabled ? undefined : onToggle}
      disabled={disabled}
      className={`w-full text-left rounded-xl border transition-all duration-150 overflow-hidden flex ${
        selected
          ? "border-indigo-400 bg-indigo-50/60 shadow-sm shadow-indigo-100"
          : disabled
          ? "border-slate-100 bg-slate-50 opacity-40 cursor-not-allowed"
          : "border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm"
      }`}
    >
      {/* Left accent bar */}
      <div className={`w-1 shrink-0 ${ls.bar} ${selected ? "opacity-100" : "opacity-30"} transition-opacity`} />

      <div className="flex items-center gap-3 flex-1 min-w-0 px-3.5 py-3">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${visual.color}`}>
          <visual.Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${ls.pill}`}>{ls.label}</span>
            <p className="font-semibold text-slate-800 text-sm truncate">{node.name}</p>
          </div>
          <p className="text-xs text-slate-400 line-clamp-1">{node.description}</p>
        </div>
        {selected
          ? <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-white" /></div>
          : <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
        }
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Match percentage arc (for psychometric results)
// ─────────────────────────────────────────────────────────────────────────────

function MatchBadge({ pct }: { pct: number }) {
  const color = pct >= 75 ? "text-emerald-600 bg-emerald-50 border-emerald-200"
              : pct >= 55 ? "text-blue-600 bg-blue-50 border-blue-200"
              : pct >= 40 ? "text-amber-600 bg-amber-50 border-amber-200"
              : "text-gray-500 bg-gray-50 border-gray-200";
  return (
    <span className={`text-sm font-bold px-2.5 py-1 rounded-full border ${color}`}>{pct}%</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase screens
// ─────────────────────────────────────────────────────────────────────────────

function IntentScreen({
  onKnow,
  onDiscover,
  onSkip,
  isAddMode,
}: {
  onKnow: () => void;
  onDiscover: () => void;
  onSkip: () => void;
  isAddMode: boolean;
}) {
  return (
    <div className="p-6 space-y-4">
      <div className="text-center space-y-1.5 pb-2">
        <p className="text-base font-bold text-slate-900">Do you have a path in mind?</p>
        <p className="text-sm text-slate-500">We'll help you find the right fit either way.</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onKnow}
          className="w-full p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30 transition-all flex items-center gap-4 group text-left"
        >
          <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <Search className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900">Yes, I know what I want</p>
            <p className="text-xs text-slate-500 mt-0.5">Search by name, field or skill — any level</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
        </button>

        <button
          onClick={onDiscover}
          className="w-full p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/30 transition-all flex items-center gap-4 group text-left"
        >
          <div className="w-11 h-11 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-900">No, help me discover</p>
            <p className="text-xs text-slate-500 mt-0.5">Use your psychometric results or describe yourself</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all shrink-0" />
        </button>
      </div>

      {!isAddMode && (
        <div className="text-center pt-2">
          <button onClick={onSkip} className="text-slate-400 hover:text-slate-600 text-sm transition-colors">
            Not sure yet — skip for now
          </button>
        </div>
      )}
    </div>
  );
}

function SearchScreen({
  picked,
  onToggle,
  onConfirm,
  saving,
  alreadySaved,
}: {
  picked: CareerPathNode[];
  onToggle: (node: CareerPathNode) => void;
  onConfirm: () => void;
  saving: boolean;
  alreadySaved: string[];
}) {
  const [query, setQuery] = useState("");
  const allNodes = useMemo(() => getAllNodes(), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allNodes;
    return allNodes.filter((node) => {
      const traitText = node.traits.map((tr) => TRAIT_NAME[tr.traitId] ?? tr.name).join(" ").toLowerCase();
      return (
        node.name.toLowerCase().includes(q) ||
        node.alias.toLowerCase().includes(q) ||
        node.description.toLowerCase().includes(q) ||
        traitText.includes(q)
      );
    });
  }, [query, allNodes]);

  const atMax = picked.length >= 2;
  const newPickedCount = countNewPickedPaths(picked, alreadySaved);

  // Group search results by level for better browsability
  const grouped = useMemo(() => {
    const broad = results.filter((n) => n.level === "broad");
    const field = results.filter((n) => n.level === "field");
    const spec  = results.filter((n) => n.level === "specialization");
    return { broad, field, spec };
  }, [results]);

  const isAlreadySaved = (node: CareerPathNode) => alreadySaved.includes(node.nodeId);

  const renderNode = (node: CareerPathNode) => {
    const saved = isAlreadySaved(node);
    return (
      <div key={node.nodeId} className="relative">
        <NodeCard
          node={node}
          selected={picked.some((p) => p.nodeId === node.nodeId)}
          onToggle={() => onToggle(node)}
          disabled={saved || (atMax && !picked.some((p) => p.nodeId === node.nodeId))}
        />
        {saved && (
          <span className="absolute top-2 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
            Already added
          </span>
        )}
      </div>
    );
  };

  const confirmLabel = saving
    ? "Saving…"
    : newPickedCount > 0
    ? `Add ${newPickedCount} path${newPickedCount > 1 ? "s" : ""}`
    : `Update ${picked.length} path${picked.length > 1 ? "s" : ""}`;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Fixed top — search input + selected pills */}
      <div className="shrink-0 px-6 pt-5 pb-3 space-y-3">
        <div>
          <p className="font-semibold text-slate-900 text-base mb-0.5">Search careers</p>
          <p className="text-xs text-slate-500 mb-3">Browse by name, field, or skill. Pick up to 2 at any level.</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. medicine, software, law, design..."
              className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl pl-9 pr-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none transition-all"
            />
            {query && (
              <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-3 bg-indigo-50/60 rounded-xl border border-indigo-100">
            <span className="text-[11px] font-semibold text-indigo-400 self-center mr-1">Selected:</span>
            {picked.map((n) => (
              <span key={n.nodeId} className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${LEVEL_STYLE[n.level].pill}`}>
                {n.name}
                <button onClick={() => onToggle(n)} className="hover:opacity-70 ml-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable results list */}
      <div className="flex-1 overflow-y-auto px-6 pb-3 space-y-3">
        {results.length === 0 && (
          <p className="text-slate-400 text-sm text-center py-8">No careers match "{query}"</p>
        )}
        {grouped.broad.length > 0 && (
          <div className="space-y-1.5">
            {query && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">Domains</p>}
            {grouped.broad.map(renderNode)}
          </div>
        )}
        {grouped.field.length > 0 && (
          <div className="space-y-1.5">
            {query && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">Fields</p>}
            {grouped.field.map(renderNode)}
          </div>
        )}
        {grouped.spec.length > 0 && (
          <div className="space-y-1.5">
            {query && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">Roles</p>}
            {grouped.spec.map(renderNode)}
          </div>
        )}
      </div>

      {/* Pinned confirm button */}
      {picked.length > 0 && (
        <div className="shrink-0 px-6 pb-5 pt-3 border-t border-slate-100">
          <button
            onClick={onConfirm}
            disabled={saving}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
          >
            {confirmLabel}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function DiscoverChoiceScreen({
  hasTakenPsychometric,
  onPsychometric,
  onExpress,
}: {
  hasTakenPsychometric: boolean;
  onPsychometric: () => void;
  onExpress: () => void;
}) {
  return (
    <div className="p-6 space-y-4">
      <div className="text-center space-y-1.5 pb-2">
        <p className="text-base font-bold text-slate-900">How should we find your match?</p>
        <p className="text-sm text-slate-500">Choose your preferred discovery method.</p>
      </div>

      <button
        onClick={onPsychometric}
        disabled={!hasTakenPsychometric}
        className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 group text-left ${
          hasTakenPsychometric
            ? "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30"
            : "border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed"
        }`}
      >
        <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
          <BookOpen className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900">Use my psychometric results</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {hasTakenPsychometric
              ? "Domains, fields & roles ranked by your trait profile"
              : "Take the psychometric assessment first to unlock this"}
          </p>
        </div>
        {hasTakenPsychometric && (
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />
        )}
      </button>

      <button
        onClick={onExpress}
        className="w-full p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 transition-all flex items-center gap-4 group text-left"
      >
        <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900">Express myself</p>
          <p className="text-xs text-slate-500 mt-0.5">Describe your interests in your own words</p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all shrink-0" />
      </button>
    </div>
  );
}

function ExpressScreen({ onResults }: { onResults: (nodes: CareerPathNode[]) => void }) {
  const [text, setText] = useState("");
  const allNodes = useMemo(() => getAllNodes(), []);

  const handleFind = () => {
    const tokens = tokenize(text);
    const scored = allNodes
      .map((node) => ({ node, score: scoreNode(node, tokens) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((r) => r.node);
    onResults(scored);
  };

  return (
    <div className="p-6 space-y-5">
      <div className="space-y-1.5 pb-1">
        <p className="text-base font-bold text-slate-900">Tell us about yourself</p>
        <p className="text-sm text-slate-500">
          Describe what interests you, what you're good at, or what kind of work excites you.
        </p>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="e.g. I love maths and figuring out how things work. I'm good with computers and enjoy solving puzzles..."
        className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-sm focus:outline-none transition-all resize-none"
      />

      <button
        onClick={handleFind}
        disabled={text.trim().length < 10}
        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
      >
        Find my matches
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function ExpressResultsScreen({
  results,
  picked,
  onToggle,
  onConfirm,
  saving,
}: {
  results: CareerPathNode[];
  picked: CareerPathNode[];
  onToggle: (node: CareerPathNode) => void;
  onConfirm: () => void;
  saving: boolean;
}) {
  const atMax = picked.length >= 2;
  return (
    <div className="p-6 space-y-4">
      <div className="mb-2">
        <p className="font-semibold text-gray-900">Your matches</p>
        <p className="text-xs text-gray-500">Based on what you described. Pick up to 2.</p>
      </div>

      {results.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-10">
          No strong matches found. Try different words.
        </p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {results.map((node) => (
            <NodeCard
              key={node.nodeId}
              node={node}
              selected={picked.some((p) => p.nodeId === node.nodeId)}
              onToggle={() => onToggle(node)}
              disabled={atMax && !picked.some((p) => p.nodeId === node.nodeId)}
            />
          ))}
        </div>
      )}

      {picked.length > 0 && (
        <button
          onClick={onConfirm}
          disabled={saving}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
        >
          {saving ? "Saving…" : `Add ${picked.length} path${picked.length > 1 ? "s" : ""}`}
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function PsychometricResultsScreen({
  allMatches,
  picked,
  onToggle,
  onConfirm,
  saving,
  traitProfile,
  alreadySaved,
}: {
  allMatches: BroadPathMatch[];
  picked: CareerPathNode[];
  onToggle: (node: CareerPathNode) => void;
  onConfirm: () => void;
  saving: boolean;
  traitProfile: TraitProfile;
  alreadySaved: string[];
}) {
  const [expandedBroadId, setExpandedBroadId] = useState<string | null>(null);
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);
  const atMax = picked.length >= 2;
  const newPickedCount = countNewPickedPaths(picked, alreadySaved);

  // Pre-compute ranked children for every node so we don't call hooks inside .map()
  const allRankedFields = useMemo(() => {
    const result: Record<string, ReturnType<typeof rankChildren>> = {};
    for (const match of allMatches) {
      const broadNode = getBroadNode(match.node.nodeId);
      if (broadNode) result[match.node.nodeId] = rankChildren(broadNode, traitProfile);
    }
    return result;
  }, [allMatches, traitProfile]);

  const allRankedSpecs = useMemo(() => {
    const result: Record<string, ReturnType<typeof rankChildren>> = {};
    for (const fields of Object.values(allRankedFields)) {
      for (const { node: fieldNode } of fields) {
        result[fieldNode.nodeId] = rankChildren(fieldNode, traitProfile);
      }
    }
    return result;
  }, [allRankedFields, traitProfile]);

  const onBroadExpand = (nodeId: string) => {
    const closing = expandedBroadId === nodeId;
    setExpandedBroadId(closing ? null : nodeId);
    if (!closing) setExpandedFieldId(null);
  };

  const matchColor = (pct: number) =>
    pct >= 75 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : pct >= 55 ? "text-indigo-700 bg-indigo-50 border-indigo-200"
    : pct >= 40 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-slate-500 bg-slate-50 border-slate-200";

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Fixed top */}
      <div className="shrink-0 px-6 pt-5 pb-3 space-y-3">
        <div>
          <p className="font-semibold text-slate-900 text-base">Your Career Landscape</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Ranked by your trait profile. Expand any domain to explore fields and specific roles. Pick up to 2.
          </p>
        </div>

        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1.5 p-3 bg-indigo-50/60 rounded-xl border border-indigo-100">
            <span className="text-[11px] font-semibold text-indigo-400 self-center mr-1">Selected:</span>
            {picked.map((n) => (
              <span key={n.nodeId} className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${LEVEL_STYLE[n.level].pill}`}>
                {n.name}
                <button onClick={() => onToggle(n)} className="hover:opacity-70 ml-0.5">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-6 pb-3 space-y-2">
        {allMatches.map((match, i) => {
          const broadNode = getBroadNode(match.node.nodeId);
          if (!broadNode) return null;
          const visual = NODE_VISUAL[match.node.nodeId] ?? DEFAULT_VISUAL;
          const isBroadSelected = picked.some((p) => p.nodeId === broadNode.nodeId);
          const isExpanded = expandedBroadId === broadNode.nodeId;
          const isBroadAlreadySaved = alreadySaved.includes(broadNode.nodeId);
          const isBroadDisabled = isBroadAlreadySaved || (atMax && !isBroadSelected);
          const pct = match.matchPercentage;
          const rankedFields = allRankedFields[broadNode.nodeId] ?? [];

          return (
            <div
              key={broadNode.nodeId}
              className={`rounded-xl border overflow-hidden transition-all duration-150 ${
                isExpanded ? "border-indigo-200 shadow-sm shadow-indigo-100/60" : "border-slate-200"
              }`}
            >
              {/* ── Broad row ── */}
              <div className={`flex items-center gap-2 p-3 ${isExpanded ? "bg-indigo-50/50" : "bg-white"}`}>
                {/* Clickable selection area */}
                <button
                  onClick={isBroadDisabled ? undefined : () => onToggle(broadNode)}
                  disabled={isBroadDisabled}
                  className={`flex items-center gap-3 flex-1 min-w-0 text-left rounded-lg p-0.5 transition-opacity ${isBroadDisabled ? "opacity-40 cursor-not-allowed" : "hover:opacity-90"}`}
                >
                  <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${visual.color}`}>
                    <visual.Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {i < 2 && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${matchColor(pct)}`}>
                          {i === 0 ? "Best match" : "Strong match"}
                        </span>
                      )}
                      <p className="font-semibold text-slate-900 text-sm">{broadNode.name}</p>
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-1 mt-0.5">{broadNode.description}</p>
                  </div>
                </button>

                {/* Right: match% + check + expand chevron */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {isBroadAlreadySaved ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
                      Added
                    </span>
                  ) : (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${matchColor(pct)}`}>{pct}%</span>
                  )}
                  {isBroadSelected && (
                    <button onClick={() => onToggle(broadNode)} className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </button>
                  )}
                  {rankedFields.length > 0 && (
                    <button
                      onClick={() => onBroadExpand(broadNode.nodeId)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        isExpanded
                          ? "bg-indigo-100 text-indigo-700"
                          : "bg-slate-100 hover:bg-indigo-100 text-slate-500 hover:text-indigo-700"
                      }`}
                    >
                      {isExpanded ? "Collapse" : "Explore"}
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                  )}
                </div>
              </div>

              {/* ── Fields (expanded) ── */}
              {isExpanded && rankedFields.length > 0 && (
                <div className="border-t border-indigo-100 bg-slate-50/70 px-3 py-2.5 space-y-1.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1 pb-1">
                    Fields within {broadNode.name}
                  </p>
                  {rankedFields.map(({ node: fieldNode, matchScore, isPositive }) => {
                    const isFieldSelected = picked.some((p) => p.nodeId === fieldNode.nodeId);
                    const isFieldDisabled = atMax && !isFieldSelected;
                    const isFieldExpanded = expandedFieldId === fieldNode.nodeId;
                    const fieldPct = Math.round(matchScore * 100);
                    const rankedSpecs = allRankedSpecs[fieldNode.nodeId] ?? [];

                    return (
                      <div
                        key={fieldNode.nodeId}
                        className={`rounded-lg border overflow-hidden ${
                          isFieldExpanded ? "border-violet-200" : "border-slate-200"
                        }`}
                      >
                        {/* Field row */}
                        <div className={`flex items-center gap-2 px-3 py-2.5 ${isFieldExpanded ? "bg-violet-50/50" : "bg-white"}`}>
                          <div className={`w-1 h-5 rounded-full shrink-0 ${isPositive ? "bg-violet-400" : "bg-slate-200"}`} />
                          <button
                            onClick={isFieldDisabled ? undefined : () => onToggle(fieldNode)}
                            disabled={isFieldDisabled}
                            className={`flex-1 min-w-0 text-left transition-opacity ${isFieldDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                          >
                            <p className="text-sm font-medium text-slate-800 truncate">{fieldNode.name}</p>
                            <p className="text-[11px] text-slate-400 truncate">{fieldNode.description}</p>
                          </button>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full border ${matchColor(fieldPct)}`}>{fieldPct}%</span>
                            {isFieldSelected && (
                              <div className="w-4 h-4 rounded-full bg-violet-600 flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                            {rankedSpecs.length > 0 && (
                              <button
                                onClick={() => setExpandedFieldId((prev) => prev === fieldNode.nodeId ? null : fieldNode.nodeId)}
                                className={`flex items-center gap-0.5 px-2 py-1 rounded-md text-[11px] font-semibold transition-all ${
                                  isFieldExpanded
                                    ? "bg-violet-100 text-violet-700"
                                    : "bg-slate-100 hover:bg-violet-100 text-slate-500 hover:text-violet-700"
                                }`}
                              >
                                {isFieldExpanded ? "Less" : "Roles"}
                                <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isFieldExpanded ? "rotate-90" : ""}`} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* ── Specs (expanded) ── */}
                        {isFieldExpanded && rankedSpecs.length > 0 && (
                          <div className="border-t border-violet-100 bg-emerald-50/30 px-3 py-2 space-y-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1 pb-0.5">
                              Roles in {fieldNode.name}
                            </p>
                            {rankedSpecs.map(({ node: specNode, matchScore: sm }) => {
                              const isSpecSelected = picked.some((p) => p.nodeId === specNode.nodeId);
                              const isSpecDisabled = atMax && !isSpecSelected;
                              const specPct = Math.round(sm * 100);
                              return (
                                <button
                                  key={specNode.nodeId}
                                  onClick={isSpecDisabled ? undefined : () => onToggle(specNode)}
                                  disabled={isSpecDisabled}
                                  className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-all ${
                                    isSpecSelected
                                      ? "border-emerald-300 bg-emerald-50"
                                      : isSpecDisabled
                                      ? "border-slate-100 opacity-40 cursor-not-allowed"
                                      : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40"
                                  }`}
                                >
                                  <div className="w-1 h-3.5 rounded-full bg-emerald-400 shrink-0" />
                                  <p className="text-xs font-medium text-slate-800 truncate flex-1">{specNode.name}</p>
                                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${matchColor(specPct)}`}>{specPct}%</span>
                                  {isSpecSelected && (
                                    <div className="w-4 h-4 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                                      <Check className="w-2.5 h-2.5 text-white" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pinned confirm button */}
      {picked.length > 0 && (
        <div className="shrink-0 px-6 pb-5 pt-3 border-t border-slate-100">
          <button
            onClick={onConfirm}
            disabled={saving}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
          >
            {saving
              ? "Saving…"
              : newPickedCount > 0
              ? `Add ${newPickedCount} path${newPickedCount > 1 ? "s" : ""}`
              : `Update ${picked.length} path${picked.length > 1 ? "s" : ""}`}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Funnel drill-down screen (reused for field AND spec levels)
// ─────────────────────────────────────────────────────────────────────────────

function FunnelScreen({
  parentNode,
  traitProfile,
  onPick,
  onSkip,
  saving,
}: {
  parentNode: CareerPathNode;
  traitProfile: TraitProfile;
  onPick: (node: CareerPathNode) => void;
  onSkip: () => void;
  saving: boolean;
}) {
  const [selected, setSelected] = useState<CareerPathNode | null>(null);
  const ranked = useMemo(() => rankChildren(parentNode, traitProfile), [parentNode, traitProfile]);
  const isField = parentNode.level === "broad";
  const childLabel = isField ? "field" : "specialization";

  const ls = isField ? LEVEL_STYLE.field : LEVEL_STYLE.specialization;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Fixed top */}
      <div className="shrink-0 px-6 pt-5 pb-3 space-y-3">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl border border-slate-200">
          <div className={`w-2 h-2 rounded-full ${LEVEL_STYLE.broad.bar}`} />
          <span className="text-xs font-semibold text-slate-700">{parentNode.name}</span>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ls.pill}`}>
            {isField ? "Pick a field" : "Pick a role"}
          </span>
        </div>
        <p className="text-sm font-medium text-slate-500">
          Select a {childLabel} or skip to stay at this level.
        </p>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-6 pb-3 space-y-2">
        {ranked.map(({ node, matchScore, isPositive }) => {
          const pct = Math.round(matchScore * 100);
          const isChosen = selected?.nodeId === node.nodeId;
          return (
            <button
              key={node.nodeId}
              onClick={() => setSelected(isChosen ? null : node)}
              className={`w-full text-left rounded-xl border-2 overflow-hidden transition-all flex ${
                isChosen
                  ? "border-indigo-400 bg-indigo-50/60 shadow-sm shadow-indigo-100"
                  : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/20"
              }`}
            >
              {/* Level accent bar */}
              <div className={`w-1 shrink-0 ${ls.bar} ${isChosen ? "opacity-100" : "opacity-30"} transition-opacity`} />
              <div className="flex items-start gap-3 flex-1 min-w-0 px-3.5 py-3">
                <div className={`w-7 h-7 rounded-lg border flex items-center justify-center shrink-0 mt-0.5 ${
                  isPositive ? "bg-emerald-50 border-emerald-200 text-emerald-600" : "bg-slate-50 border-slate-200 text-slate-500"
                }`}>
                  <Target className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="font-semibold text-slate-900 text-sm truncate">{node.name}</p>
                    <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full border ${
                      isPositive
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-slate-50 border-slate-200 text-slate-500"
                    }`}>{pct}%</span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-1">{node.description}</p>
                  {node.traits.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {node.traits.slice(0, 3).map((tr) => (
                        <span key={tr.traitId} className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100 font-medium">
                          {TRAIT_NAME[tr.traitId] ?? tr.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {isChosen && (
                  <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Pinned action buttons */}
      <div className="shrink-0 px-6 pb-5 pt-3 border-t border-slate-100 space-y-2">
        {selected && (
          <button
            onClick={() => onPick(selected)}
            disabled={saving}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
          >
            {saving ? "Saving…" : `Continue with ${selected.name}`}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onSkip}
          disabled={saving}
          className="w-full py-2.5 text-sm text-slate-400 hover:text-slate-700 transition-colors"
        >
          {saving ? "Saving…" : `Stay at ${isField ? "domain" : "field"} level`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_ARR: string[] = [];

// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function BroadPathSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAddMode = searchParams.get("mode") === "add";

  const storedTraitProfile = StudentStore((s) => s.student?.traitProfile);

  // Prefer traits passed via navigation state (fresh from assessment), fall back
  // to the student's stored trait profile so percentages always reflect real data.
  const traitProfile: TraitProfile =
    (location.state?.traitProfile && Object.keys(location.state.traitProfile).length > 0)
      ? location.state.traitProfile
      : storedTraitProfile ?? {};

  const hasTakenPsychometric = useMemo(
    () => storedTraitProfile != null || Object.keys(traitProfile).length > 0,
    [traitProfile, storedTraitProfile]
  );

  const allMatches = useMemo(
    () => getTopBroadPaths(traitProfile, BROAD_PATH_NODES, 8, 0),
    [traitProfile]
  );

  const preselectNodeId: string | undefined = location.state?.preselectNodeId;

  const [phase, setPhase] = useState<Phase>("intent");
  const [picked, setPicked] = useState<CareerPathNode[]>(() => {
    // If navigated here with a pre-selected node, initialise picked immediately
    // so the search screen renders it as already ticked.
    if (preselectNodeId) {
      const node = getAllNodes().find((n) => n.nodeId === preselectNodeId);
      return node ? [node] : [];
    }
    return [];
  });
  const [expressResults, setExpressResults] = useState<CareerPathNode[]>([]);
  const [funnelBroadNode, setFunnelBroadNode] = useState<CareerPathNode | null>(null);
  const [funnelFieldNode, setFunnelFieldNode] = useState<CareerPathNode | null>(null);

  // If a node was pre-selected (navigated from overview panel), skip straight to
  // the spec-funnel (for field nodes) or search (for other levels).
  const [didHandlePreselect, setDidHandlePreselect] = useState(false);
  useEffect(() => {
    if (!preselectNodeId || didHandlePreselect) return;
    setDidHandlePreselect(true);
    const node = getAllNodes().find((n) => n.nodeId === preselectNodeId);
    if (!node) return;
    if (node.level === "field") {
      const broadNode = getBroadNode(node.parentId ?? "");
      if (broadNode) {
        setFunnelBroadNode(broadNode);
        setFunnelFieldNode(node);
        setPhase("spec_funnel");
      } else {
        setPhase("search");
      }
    } else if (node.level === "specialization") {
      // Jump straight to search; node is already in picked
      setPhase("search");
    } else {
      // Broad node — offer field funnel
      setFunnelBroadNode(node);
      setFunnelFieldNode(null);
      setPhase("field_funnel");
    }
  }, [preselectNodeId, didHandlePreselect]);

  const { save, saving } = useSaveSelection(traitProfile);

  // Already-saved broad path IDs — used to mark them as disabled/added in selection screens
  const alreadySaved: string[] = StudentStore((s) => s.student?.selectedBroadPaths ?? EMPTY_ARR);

  // Current student active nodes — used to intercept graduation-required selections
  const currentActiveNodes = StudentStore((s) => s.student?.activeNodes ?? EMPTY_ARR);

  // Graduation confirmation: set when the user picks a child node whose direct parent is active
  const [graduationCandidate, setGraduationCandidate] = useState<{
    childNode: CareerPathNode;
    parentNode: CareerPathNode;
  } | null>(null);

  // Overlay state: tracks which path names are being saved for the animation
  const [savePhase, setSavePhase] = useState<"idle" | "saving" | "done">("idle");
  const [saveNames, setSaveNames] = useState<string[]>([]);

  const dest = isAddMode ? "/career-path" : "/dashboard";

  const triggerSave = async (nodes: CareerPathNode[]) => {
    const names = nodes.map((n) => n.name);
    setSaveNames(names);
    setSavePhase("saving");
    const ok = await save(nodes);
    if (ok) {
      setSavePhase("done");
      setTimeout(() => navigate(dest), 1100);
    } else {
      setSavePhase("idle");
    }
  };

  const toggleNode = (node: CareerPathNode) => {
    // Deselect always works
    if (picked.some((p) => p.nodeId === node.nodeId)) {
      setPicked((prev) => prev.filter((p) => p.nodeId !== node.nodeId));
      return;
    }
    // If the node's direct parent is currently active in the student's profile,
    // they must graduate through it — show the graduation confirmation modal.
    if (node.parentId) {
      const parentIsActive = currentActiveNodes.some(
        (n) => n.nodeId === node.parentId && n.status === "active",
      );
      if (parentIsActive) {
        const parentNode = getAllNodes().find((n) => n.nodeId === node.parentId);
        if (parentNode) {
          setGraduationCandidate({ childNode: node, parentNode });
          return;
        }
      }
    }
    setPicked((prev) => {
      if (prev.length >= 2) return prev;
      return [...prev, node];
    });
  };

  // Graduated save: include any already-picked items plus the graduating child
  const confirmGraduation = () => {
    if (!graduationCandidate) return;
    const { childNode } = graduationCandidate;
    setGraduationCandidate(null);
    const combined = [...picked.filter((p) => p.nodeId !== childNode.nodeId), childNode];
    triggerSave(combined);
  };

  // After express confirms picks: if exactly one broad was selected,
  // offer the field-level funnel. Multiple broads → save directly.
  const confirmOrFunnel = (currentPicked: CareerPathNode[]) => {
    const broadPicks = currentPicked.filter((n) => n.level === "broad");
    if (broadPicks.length === 1 && currentPicked.every((n) => n.level === "broad")) {
      setFunnelBroadNode(broadPicks[0]);
      setFunnelFieldNode(null);
      setPhase("field_funnel");
    } else {
      triggerSave(currentPicked);
    }
  };

  const confirmFunnelField = (fieldNode: CareerPathNode) => {
    setFunnelFieldNode(fieldNode);
    setPhase("spec_funnel");
  };

  const skipFunnelField = () => triggerSave(picked);

  const confirmFunnelSpec = (specNode: CareerPathNode) => {
    const chain: CareerPathNode[] = [];
    if (funnelBroadNode) chain.push(funnelBroadNode);
    if (funnelFieldNode) chain.push(funnelFieldNode);
    chain.push(specNode);
    triggerSave([...picked.filter((p) => p.nodeId !== funnelBroadNode?.nodeId), ...chain]);
  };

  const skipFunnelSpec = () => {
    const chain: CareerPathNode[] = [];
    if (funnelBroadNode) chain.push(funnelBroadNode);
    if (funnelFieldNode) chain.push(funnelFieldNode);
    triggerSave([...picked.filter((p) => p.nodeId !== funnelBroadNode?.nodeId), ...chain]);
  };

  const goBack = () => {
    if (phase === "spec_funnel") setPhase("field_funnel");
    else if (phase === "field_funnel") {
      setFunnelBroadNode(null);
      setFunnelFieldNode(null);
      setPhase("psychometric");
    }
    else if (phase === "search" || phase === "discover_choice") setPhase("intent");
    else if (phase === "psychometric" || phase === "express") setPhase("discover_choice");
    else if (phase === "express_results") setPhase("express");
    else setPhase("intent");
  };

  const PHASE_TITLES: Record<Phase, string> = {
    intent: "Path Discovery",
    search: "Search Careers",
    discover_choice: "How to Discover",
    psychometric: "Your Results",
    express: "Express Yourself",
    express_results: "Your Matches",
    field_funnel: "Choose a Field",
    spec_funnel: "Choose a Specialisation",
  };

  const stepCount: Record<Phase, number> = {
    intent: 1, discover_choice: 2, search: 2,
    psychometric: 3, express: 3, express_results: 3,
    field_funnel: 4, spec_funnel: 5,
  };
  const currentStep = stepCount[phase];
  const totalSteps = 5;

  // Breadcrumb trail — shows the funnel path being built
  const breadcrumb: string[] = [];
  if (funnelBroadNode) breadcrumb.push(funnelBroadNode.name);
  if (funnelFieldNode) breadcrumb.push(funnelFieldNode.name);

  return (
    <ProtectedRoute>
      <div className="flex h-screen overflow-hidden bg-slate-100">
        <Sidebar />

        <main className="flex-1 lg:ml-64 flex flex-col min-h-0 overflow-hidden">
          {/* Top bar */}
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <button
                onClick={() => navigate(isAddMode ? "/career-path" : "/dashboard")}
                className="hover:text-slate-800 transition-colors font-medium"
              >
                {isAddMode ? "Career Paths" : "Dashboard"}
              </button>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
              <span className="text-slate-800 font-semibold">{PHASE_TITLES[phase]}</span>
              {breadcrumb.length > 0 && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                  <span className="text-indigo-600 font-medium truncate max-w-xs">
                    {breadcrumb.join(" → ")}
                  </span>
                </>
              )}
            </div>

            {/* Step indicators */}
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i < currentStep ? "bg-indigo-500 w-5" : "bg-slate-200 w-2"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content area — fills all remaining height */}
          <div className="flex-1 flex justify-center p-4 md:p-6 min-h-0">
            <div className="w-full max-w-3xl flex flex-col flex-1 min-h-0">

              {/* Card — fills height so the list can scroll inside it */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col flex-1 overflow-hidden">
                {/* Card header */}
                <div className="px-6 pt-5 pb-4 border-b border-slate-100 shrink-0">
                  <div className="flex items-start gap-4">
                    {phase !== "intent" && (
                      <button
                        onClick={goBack}
                        className="mt-0.5 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-700 transition-colors shrink-0"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Back
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold text-slate-900">{PHASE_TITLES[phase]}</h2>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {isAddMode ? "Add a career path to your journey" : "Help us personalise your learning experience"}
                      </p>
                    </div>
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
                      <Target className="w-4.5 h-4.5 text-indigo-600" />
                    </div>
                  </div>
                </div>

        {/* Phase content — fills remaining card height; each screen controls its own scroll */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {phase === "intent" && (
          <IntentScreen
            onKnow={() => setPhase("search")}
            onDiscover={() => setPhase("discover_choice")}
            onSkip={() => navigate(isAddMode ? "/career-path" : "/dashboard")}
            isAddMode={isAddMode}
          />
        )}
        {phase === "search" && (
          <SearchScreen picked={picked} onToggle={toggleNode} onConfirm={() => triggerSave(picked)} saving={saving} alreadySaved={alreadySaved} />
        )}
        {phase === "discover_choice" && (
          <DiscoverChoiceScreen
            hasTakenPsychometric={hasTakenPsychometric}
            onPsychometric={() => setPhase("psychometric")}
            onExpress={() => setPhase("express")}
          />
        )}
        {phase === "psychometric" && (
          <PsychometricResultsScreen
            allMatches={allMatches}
            picked={picked}
            onToggle={toggleNode}
            onConfirm={() => triggerSave(picked)}
            saving={saving}
            traitProfile={traitProfile}
            alreadySaved={alreadySaved}
          />
        )}
        {phase === "express" && (
          <ExpressScreen onResults={(nodes) => { setExpressResults(nodes); setPhase("express_results"); }} />
        )}
        {phase === "express_results" && (
          <ExpressResultsScreen
            results={expressResults}
            picked={picked}
            onToggle={toggleNode}
            onConfirm={() => confirmOrFunnel(picked)}
            saving={saving}
          />
        )}
        {phase === "field_funnel" && funnelBroadNode && (
          <FunnelScreen
            parentNode={funnelBroadNode}
            traitProfile={traitProfile}
            onPick={confirmFunnelField}
            onSkip={skipFunnelField}
            saving={saving}
          />
        )}
        {phase === "spec_funnel" && funnelFieldNode && (
          <FunnelScreen
            parentNode={funnelFieldNode}
            traitProfile={traitProfile}
            onPick={confirmFunnelSpec}
            onSkip={skipFunnelSpec}
            saving={saving}
          />
        )}
        </div>{/* /scrollable body */}
              </div>{/* /card */}
            </div>{/* /max-w-3xl */}
          </div>{/* /content area */}
        </main>
      </div>

      {/* Graduation confirmation modal */}
      {graduationCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full space-y-4">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto">
                <GraduationCap className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg">Graduation required</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                To select{" "}
                <span className="font-semibold text-slate-700">{graduationCandidate.childNode.name}</span>
                , you must first graduate from{" "}
                <span className="font-semibold text-slate-700">{graduationCandidate.parentNode.name}</span>
                . Graduating marks <span className="font-semibold text-slate-700">{graduationCandidate.parentNode.name}</span>{" "}
                as complete and commits you to this path.
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setGraduationCandidate(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 font-semibold text-sm transition-all"
              >
                Not now
              </button>
              <button
                onClick={confirmGraduation}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-sm transition-all"
              >
                Graduate &amp; Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saving overlay — shown while persisting and briefly after */}
      {savePhase !== "idle" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-5 mx-4 max-w-sm w-full">
            {savePhase === "saving" ? (
              <>
                <div className="w-14 h-14 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                <div className="text-center">
                  <p className="font-bold text-slate-900 text-lg">
                    Adding {saveNames.length} path{saveNames.length > 1 ? "s" : ""}…
                  </p>
                  <div className="mt-3 space-y-1">
                    {saveNames.map((name) => (
                      <div key={name} className="flex items-center justify-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                        <p className="text-sm text-slate-600">{name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-7 h-7 text-emerald-600" />
                </div>
                <div className="text-center">
                  <p className="font-bold text-slate-900 text-lg">
                    {saveNames.length > 1 ? "Paths added!" : "Path added!"}
                  </p>
                  <div className="mt-2 space-y-1">
                    {saveNames.map((name) => (
                      <p key={name} className="text-sm text-slate-500">{name}</p>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </ProtectedRoute>
  );
}
